/**
 * Leave Request Create Page — structured form with DateRangePicker.
 *
 * Calls POST /v1/hros/leave/requests via BLL.
 * Fields: leave_type, start_date, end_date, reason.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import {
  Button,
  Card,
  CardContent,
  DateRangePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Types ────────────────────────────────────────────────────────────────────

interface LeaveType {
  id: string;
  name: string;
  max_days?: number;
}

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string, body: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return res.json();
}

// ── Component ────────────────────────────────────────────────────────────────

export function LeaveCreatePage() {
  const navigate = useNavigate();

  const [leaveTypeId, setLeaveTypeId] = React.useState('');
  const [startDate, setStartDate] = React.useState<string | null>(null);
  const [endDate, setEndDate] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  // Load leave types
  const { data: ltData } = useList<LeaveType>({
    resource: 'leave_types',
    pagination: { pageSize: 50 },
    sorters: [{ field: 'name', order: 'asc' }],
  });
  const leaveTypes = (ltData?.data ?? []) as LeaveType[];

  // Calculate days
  const dayCount = React.useMemo(() => {
    if (!startDate || !endDate) return 0;
    const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
    return Math.max(1, Math.round(ms / 86_400_000) + 1);
  }, [startDate, endDate]);

  const today = new Date().toISOString().split('T')[0];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!leaveTypeId) { setError('Please select a leave type.'); return; }
    if (!startDate) { setError('Please select a start date.'); return; }
    if (!endDate) { setError('Please select an end date.'); return; }

    setSubmitting(true);
    try {
      const res = await callBLL('/v1/hros/leave/requests', {
        leave_type_id: leaveTypeId,
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim() || undefined,
      });
      if (res.success) {
        navigate(`/leave/${res.data.id}`);
      } else {
        setError(res.error?.message ?? 'Failed to submit leave request.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leave')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Apply for Leave</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="pt-5 space-y-5">
            {/* Leave Type */}
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Leave Type <span className="text-destructive">*</span>
              </label>
              <Select value={leaveTypeId} onValueChange={setLeaveTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type…" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((lt) => (
                    <SelectItem key={lt.id} value={lt.id}>
                      {lt.name}{lt.max_days ? ` (max ${lt.max_days} days)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Leave Dates <span className="text-destructive">*</span>
              </label>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                startLabel="From"
                endLabel="To"
                minDate={today}
                required
              />
              {dayCount > 0 && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  {dayCount} day{dayCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Reason <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Brief reason for leave…"
                rows={3}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/leave')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default LeaveCreatePage;
